import axios from 'axios';
import { generateCompletePreviewReportPDF, generateCompletePreviewReportPDFAdditional, generateCompletePreviewReportPDFPPPFFL } from '../utils/pdfGenerator';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Generate Preview Report PDF using the complete PDF generator
 * @param {Object} reportData - The data needed to generate the report
 * @param {string} appId - Application ID
 * @param {string} orgId - Organization ID
 * @param {boolean} includeObservations - Whether to include observations column
 * @returns {Promise<Blob>} - PDF Blob
 */
export const generatePreviewReportPDF = async (reportData, appId, orgId, includeObservations = false) => {
  try {
    // Use the complete PDF generator utility
    const doc = generateCompletePreviewReportPDF(reportData, includeObservations);

    // Return PDF as Blob - ensure it's fully generated
    console.log('📦 Creating PDF blob...');
    const pdfBlob = doc.output('blob');
    console.log(`✅ PDF blob created with ALL sections. Size: ${(pdfBlob.size / 1024).toFixed(2)} KB`);

    return pdfBlob;
  } catch (error) {
    console.error('❌ Error generating preview report PDF:', error);
    throw error;
  }
};

export const generatePreviewReportPDFPPPFFL = async (reportData, appId, orgId, includeObservations = false) => {
  try {
    // Use the complete PDF generator utility
    const doc = generateCompletePreviewReportPDFPPPFFL(reportData, includeObservations);

    // Return PDF as Blob - ensure it's fully generated
    console.log('📦 Creating PDF blob...');
    const pdfBlob = doc.output('blob');
    console.log(`✅ PDF blob created with ALL sections. Size: ${(pdfBlob.size / 1024).toFixed(2)} KB`);

    return pdfBlob;
  } catch (error) {
    console.error('❌ Error generating preview report PDF:', error);
    throw error;
  }
};

export const generatePreviewReportPDFAdditional = async (reportData, appId, orgId, assessmentTypeId, includeObservations = false) => {
  try {
    // Use the complete PDF generator utility
    const doc = generateCompletePreviewReportPDFAdditional(reportData, assessmentTypeId, includeObservations);

    // Return PDF as Blob - ensure it's fully generated
    console.log('📦 Creating PDF blob...');
    const pdfBlob = doc.output('blob');
    console.log(`✅ PDF blob created with ALL sections. Size: ${(pdfBlob.size / 1024).toFixed(2)} KB`);

    return pdfBlob;
  } catch (error) {
    console.error('❌ Error generating preview report PDF:', error);
    throw error;
  }
};

/**
 * Upload PDF file to S3
 * @param {Blob} pdfBlob - PDF file as Blob
 * @param {string} filename - Filename for the PDF
 * @returns {Promise<string>} - Uploaded file path
 */
export const uploadPDFToS3 = async (pdfBlob, filename) => {
  try {
    const formData = new FormData();
    formData.append('file', pdfBlob, filename);

    const response = await axios.post(`${API_URL}/files/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (response.data.success && response.data.data) {
      return response.data.data.key || response.data.data.path || response.data.data.fileName;
    } else {
      throw new Error('Failed to upload file to S3');
    }
  } catch (error) {
    console.error('Error uploading PDF to S3:', error);
    throw error;
  }
};

/**
 * Save preview report record to database
 * @param {Object} reportInfo - Report information
 * @returns {Promise<Object>} - Save result
 */
export const savePreviewReportRecord = async (reportInfo) => {
  try {
    const response = await axios.post(`${API_URL}/preview-report/save-report`, reportInfo);
    return response.data;
  } catch (error) {
    console.error('Error saving preview report record:', error);
    throw error;
  }
};

/**
 * Generate, upload, and save preview report
 * @param {string} appId - Application ID
 * @param {string} orgId - Organization ID
 * @param {string} labTypeId - Lab type ID
 * @param {boolean} includeObservations - Whether to include observations
 * @returns {Promise<Object>} - Complete result
 */
export const generateAndSavePreviewReport = async (orgId, appId, labTypeId, fileType, includeObservations = false) => {
  try {
    console.log('🔄 Starting preview report generation and save process...');

    // 1. Fetch report data
    console.log('📥 Fetching report data...');
    const response = await axios.get(`${API_URL}/preview-report/${appId}/${orgId}`);

    if (!response.data.success) {
      throw new Error('Failed to fetch report data');
    }

    const reportData = response.data.data;
    console.log('✅ Report data fetched successfully');

    // 2. Generate filename for PDF
    const now = new Date();
    const dateStr = `${now.getMonth() + 1}-${now.getDate()}-${now.getFullYear()}`;
    const hours = now.getHours().toString().padStart(2, "0");
    const minutes = now.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const hour12 = (hours % 12 || 12).toString().padStart(2, "0");
    const timeStr = `${hour12}-${minutes}${ampm}`;
    const filename = `PreviewReport_${appId}-${dateStr}_${timeStr}.pdf`;

    // 3. Generate PDF completely
    const pdfBlob = await generatePreviewReportPDF(reportData, appId, orgId, includeObservations);
    console.log(`✅ PDF generated successfully. Size: ${(pdfBlob.size / 1024).toFixed(2)} KB`);

    // Verify PDF was generated correctly
    if (!pdfBlob || pdfBlob.size === 0) {
      throw new Error('PDF generation failed - empty or invalid PDF');
    }

    // 4. Upload to S3 only after PDF is fully generated
    console.log('☁️  Uploading PDF to S3...');
    const uploadedPath = await uploadPDFToS3(pdfBlob, filename);
    console.log(`✅ PDF uploaded to S3: ${uploadedPath}`);

    // 5. Save to database only after successful upload
    console.log('💾 Saving to database...');
    const saveResult = await savePreviewReportRecord({
      orgId,
      appId,
      labTypeId,
      path: uploadedPath,
      fileType: fileType
      // count is automatically calculated by the backend
    });
    console.log('✅ Record saved to database');

    console.log('✅ Preview report generation, upload, and save completed successfully!');

    return {
      success: true,
      filename,
      path: uploadedPath,
      size: pdfBlob.size,
      saveResult
    };
  } catch (error) {
    console.error('❌ Error in generateAndSavePreviewReport:', error);
    throw error;
  }
};
export const generateAndSavePreviewReport73 = async (orgId, appId, labTypeId, fileType, includeObservations = false) => {
  try {
    console.log('🔄 Starting preview report generation and save process...');

    // 1. Fetch report data
    console.log('📥 Fetching report data...');
    const response = await axios.get(`${API_URL}/preview-report73/${appId}/${orgId}`);

    if (!response.data.success) {
      throw new Error('Failed to fetch report data');
    }

    const reportData = response.data.data;
    console.log('✅ Report data fetched successfully');

    // 2. Generate filename for PDF
    const now = new Date();
    const dateStr = `${now.getMonth() + 1}-${now.getDate()}-${now.getFullYear()}`;
    const hours = now.getHours().toString().padStart(2, "0");
    const minutes = now.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const hour12 = (hours % 12 || 12).toString().padStart(2, "0");
    const timeStr = `${hour12}-${minutes}${ampm}`;
    const filename = `PreviewReport_${appId}-${dateStr}_${timeStr}.pdf`;

    // 3. Generate PDF completely
    const pdfBlob = await generatePreviewReportPDF(reportData, appId, orgId, includeObservations);
    console.log(`✅ PDF generated successfully. Size: ${(pdfBlob.size / 1024).toFixed(2)} KB`);

    // Verify PDF was generated correctly
    if (!pdfBlob || pdfBlob.size === 0) {
      throw new Error('PDF generation failed - empty or invalid PDF');
    }

    // 4. Upload to S3 only after PDF is fully generated
    console.log('☁️  Uploading PDF to S3...');
    const uploadedPath = await uploadPDFToS3(pdfBlob, filename);
    console.log(`✅ PDF uploaded to S3: ${uploadedPath}`);

    // 5. Save to database only after successful upload
    console.log('💾 Saving to database...');
    const saveResult = await savePreviewReportRecord({
      orgId,
      appId,
      labTypeId,
      path: uploadedPath,
      fileType: fileType
      // count is automatically calculated by the backend
    });
    console.log('✅ Record saved to database');

    console.log('✅ Preview report generation, upload, and save completed successfully!');

    return {
      success: true,
      filename,
      path: uploadedPath,
      size: pdfBlob.size,
      saveResult
    };
  } catch (error) {
    console.error('❌ Error in generateAndSavePreviewReport:', error);
    throw error;
  }
};

export const previewreportfile = async (appId, fileType = 'PreviewReport') => {
  try {
    const response = await axios.get(`${API_URL}/preview-report/reportfile`, {
      params: { appId, fileType }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching preview report file:', error);
    
    // Handle specific case when preview report is not found
    if (error.response?.status === 404 && error.response?.data?.message === 'Preview report not found') {
      return {
        success: false,
        message: 'Preview report not found',
        notFound: true
      };
    }
    
    throw error;
  }
};
export const form131File = async (appId) => {
  try {
    const response = await axios.get(`${API_URL}/preview-report/form131`, {
      params: { appId }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching fetchForm131Report file:', error);
    
    // Handle specific case when fetchForm131Report is not found
    if (error.response?.status === 404 && error.response?.data?.message === 'fetchForm131Report not found') {
      return {
        success: false,
        message: 'fetchForm131Report not found',
        notFound: true
      };
    }
    
    throw error;
  }
};

export const generateAndSavePreviewReportPPPFFL = async (orgId, appId, labTypeId, fileType, includeObservations = false) => {
  try {
    console.log('🔄 Starting preview report generation and save process...');

    // 1. Fetch report data
    console.log('📥 Fetching report data...');
    const response = await axios.get(`${API_URL}/preview-report/${appId}/${orgId}`);

    if (!response.data.success) {
      throw new Error('Failed to fetch report data');
    }

    const reportData = response.data.data;
    console.log('✅ Report data fetched successfully');

    // 2. Generate filename for PDF
    const now = new Date();
    const dateStr = `${now.getMonth() + 1}-${now.getDate()}-${now.getFullYear()}`;
    const hours = now.getHours().toString().padStart(2, "0");
    const minutes = now.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const hour12 = (hours % 12 || 12).toString().padStart(2, "0");
    const timeStr = `${hour12}-${minutes}${ampm}`;
    const filename = `PreviewReport_${appId}-${dateStr}_${timeStr}.pdf`;

    // 3. Generate PDF completely
    const pdfBlob = await generatePreviewReportPDFPPPFFL(reportData, appId, orgId, includeObservations);
    console.log(`✅ PDF generated successfully. Size: ${(pdfBlob.size / 1024).toFixed(2)} KB`);

    // Verify PDF was generated correctly
    if (!pdfBlob || pdfBlob.size === 0) {
      throw new Error('PDF generation failed - empty or invalid PDF');
    }

    // 4. Upload to S3 only after PDF is fully generated
    console.log('☁️  Uploading PDF to S3...');
    const uploadedPath = await uploadPDFToS3(pdfBlob, filename);
    console.log(`✅ PDF uploaded to S3: ${uploadedPath}`);

    // 5. Save to database only after successful upload
    console.log('💾 Saving to database...');
    const saveResult = await savePreviewReportRecord({
      orgId,
      appId,
      labTypeId,
      path: uploadedPath,
      fileType: fileType
      // count is automatically calculated by the backend
    });
    console.log('✅ Record saved to database');

    console.log('✅ Preview report generation, upload, and save completed successfully!');

    return {
      success: true,
      filename,
      path: uploadedPath,
      size: pdfBlob.size,
      saveResult
    };
  } catch (error) {
    console.error('❌ Error in generateAndSavePreviewReport:', error);
    throw error;
  }
};

export const generateAndSavePreviewReportAdditional = async (orgId, appId, labTypeId, fileType, assessmentTypeId, includeObservations = false) => {
  try {
    console.log('🔄 Starting preview report generation and save process...');

    // 1. Fetch report data
    console.log('📥 Fetching report data...');
    const response = await axios.get(`${API_URL}/preview-report/${appId}/${orgId}`);

    if (!response.data.success) {
      throw new Error('Failed to fetch report data');
    }

    const reportData = response.data.data;
    console.log('✅ Report data fetched successfully');

    // 2. Generate filename for PDF
    const now = new Date();
    const dateStr = `${now.getMonth() + 1}-${now.getDate()}-${now.getFullYear()}`;
    const hours = now.getHours().toString().padStart(2, "0");
    const minutes = now.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const hour12 = (hours % 12 || 12).toString().padStart(2, "0");
    const timeStr = `${hour12}-${minutes}${ampm}`;
    const filename = `PreviewReport_${appId}-${dateStr}_${timeStr}.pdf`;

    // 3. Generate PDF completely
    const pdfBlob = await generatePreviewReportPDFAdditional(reportData, appId, orgId, assessmentTypeId, includeObservations);
    console.log(`✅ PDF generated successfully. Size: ${(pdfBlob.size / 1024).toFixed(2)} KB`);

    // Verify PDF was generated correctly
    if (!pdfBlob || pdfBlob.size === 0) {
      throw new Error('PDF generation failed - empty or invalid PDF');
    }

    // 4. Upload to S3 only after PDF is fully generated
    console.log('☁️  Uploading PDF to S3...');
    const uploadedPath = await uploadPDFToS3(pdfBlob, filename);
    console.log(`✅ PDF uploaded to S3: ${uploadedPath}`);

    // 5. Save to database only after successful upload
    console.log('💾 Saving to database...');
    const saveResult = await savePreviewReportRecord({
      orgId,
      appId,
      labTypeId,
      path: uploadedPath,
      fileType: fileType
      // count is automatically calculated by the backend
    });
    console.log('✅ Record saved to database');

    console.log('✅ Preview report generation, upload, and save completed successfully!');

    return {
      success: true,
      filename,
      path: uploadedPath,
      size: pdfBlob.size,
      saveResult
    };
  } catch (error) {
    console.error('❌ Error in generateAndSavePreviewReport:', error);
    throw error;
  }
};

export default {
  generatePreviewReportPDF,
  uploadPDFToS3,
  previewreportfile,
  savePreviewReportRecord,
  generateAndSavePreviewReport,
  form131File,
  generateAndSavePreviewReport73,
  generateAndSavePreviewReportPPPFFL,
  generateAndSavePreviewReportAdditional
};
